import {Component, computed, inject, input, linkedSignal, output, resource, signal, untracked} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {NgTemplateOutlet} from '@angular/common';
import {DomSanitizer} from '@angular/platform-browser';
import {find as _find} from 'lodash';
import {take} from 'rxjs/operators';
import {DxCheckBoxModule, DxPopupModule} from 'devextreme-angular';
import {confirm} from 'devextreme/ui/dialog';

import {SharedModule} from '../../../../shared/shared.module';
import {DeclinaisonDenreeComponent} from './declinaison-denree/declinaison-denree.component';
import {DeclinaisonDenreeData} from './declinaison-denree/declinaison-denree-data';
import {DeclinaisonPlatComponent} from './declinaison-plat/declinaison-plat.component';
import {DeclinaisonPlatData} from './declinaison-plat/declinaison-plat-data';
import {DialogChangeUniteTechniqueComponent} from './dialog-change-unite-technique/dialog-change-unite-technique.component';
import {DialogGestionStockMinimumComponent} from './dialog-gestion-stock-minimum/dialog-gestion-stock-minimum.component';
import {DeclinaisonHelpComponent} from './declinaison-help/declinaison-help.component';
import {GenericDatagridService} from '../../../../core/services/generics/generic-datagrid.service';
import {ResponseWrapper} from '../../../../core/suppliers/wrappers/response-wrapper';
import {IsDeletableObject} from '../../../../core/models/is-deletable-object';
import {UtilsService} from '../../../../core/utils/utils.service';
import {ProduitDeclinaisonDTO} from '../../../../core/dtos/produit-declinaison-dto';
import {
  DialogProduitDeclinaisonSupplier,
  ProduitDeclinaisonService,
  WARNING_UNITE_DE_MESURE
} from '../../../../core/services/entities/produit-declinaison.service';
import {HAS_GEF, MSG_KEY, MSG_SEVERITY} from '../../../../core/constants';
import {RoutemapService} from '../../../../core/services/routemap.service';
import {ToastService} from '../../../../core/services/technique/toast.service';
import {TextMessageService} from '../../../../shared/ui/text-messages/textMessage.service';
import {FamilleEgalimService} from '../../../../core/services/entities/famille-egalim.service';
import {StockService} from '../../../../core/services/entities/stock-service';
import {FamilleFacturationService} from '../../../../core/services/entities/famille-facturation.service';
import {Auth2Service} from '../../../../core/services/security/auth2.service';
import {ButtonThemeEnum} from '../../../../shared/ui/button/button-theme-enum';
import {DeclinaisonsService} from '../../../../core/services/entities/declinaisons.service';
import {TaxesService} from '../../../../core/services/entities/taxes.service';
import {UnitesDeMesuresService} from '../../../../core/services/entities/unites-de-mesures.service';
import {ZonesDeStockageService} from '../../../../core/services/entities/zones-de-stockage.service';
import {TachesService} from '../../../../core/services/entities/taches.service';
import {CompteComptableService} from '../../../../core/services/entities/compte-comptable.service';
import {RegimeAlimentaireService} from '../../../../core/services/entities/regime-alimentaire.service';
import {ProduitsService} from '../../../../core/services/entities/produits.service';
import {TYPE_TACHE} from '../../../../core/services/entities/type-tache.service';
import {ContrainteModel} from '../../../../core/models/contrainte-model';
import {ProduitDeclinaisonContrainteAlimDTO} from '../../../../core/dtos/produit-declinaison-contrainte-alim-dto';
import {TacheDTO} from '../../../../core/dtos/tache-dto';

type ContrainteModelWithId = ContrainteModel & { id: string };

interface DeclinaisonFormData {
  libelle: string;
  libellelong: string;
  code: string;
  reference: string;
  codealim: number;
  actif: boolean;
  complementaire: boolean;
  ratioUniteTechnique: number;
  inverseRatioTechnique: number;
  ratioVolume: number;
  dlc: number;
  prixDeVente: number;
  stockMinimum: number;
  // Sélections (lookups) — objet sélectionné, source de vérité unique
  declinaison: any;
  compteComptable: any;
  taxe: any;
  uniteDeMesure: any;
  zoneDeStockage: any;
  tacheApprovisionnement: TacheDTO;
  tacheSortie: TacheDTO;
  tacheFabrication: TacheDTO;
  familleEgalim: any;
  familleFacturation: any;
  regimesIds: string[];
}

@Component({
  selector: 'yo-declinaison-produit-dialog-edit',
  templateUrl: './declinaison-produit-dialog-edit.component.html',
  styleUrls: ['./declinaison-produit-dialog-edit.component.scss'],
  standalone: true,
  imports: [
    SharedModule,
    DxPopupModule,
    NgTemplateOutlet,
    DxCheckBoxModule,
    DeclinaisonHelpComponent,
    DeclinaisonDenreeComponent,
    DeclinaisonPlatComponent,
    DialogChangeUniteTechniqueComponent,
    DialogGestionStockMinimumComponent
  ]
})
export class DeclinaisonProduitDialogEditComponent {

  public readonly ButtonThemeEnum = ButtonThemeEnum;

  // --- Entrées ---
  denree = input<boolean>();

  // --- Sorties ---
  deleted = output<number>();

  // --- Injections ---
  #routeMapSvc = inject(RoutemapService);
  #stockSvc = inject(StockService);
  #produitDeclinaisonSvc = inject(ProduitDeclinaisonService);
  #textMessageService = inject(TextMessageService);
  public gds = inject(GenericDatagridService);
  public domSanitizer = inject(DomSanitizer);
  public utils = inject(UtilsService);
  #toastSvc = inject(ToastService);
  #familleEgalimSvc = inject(FamilleEgalimService);
  #auth2Svc = inject(Auth2Service);
  #familleFacturationSvc = inject(FamilleFacturationService);
  #declinaisonsSvc = inject(DeclinaisonsService);
  #taxesSvc = inject(TaxesService);
  #udmSvc = inject(UnitesDeMesuresService);
  #zdsSvc = inject(ZonesDeStockageService);
  #tacheSvc = inject(TachesService);
  #compteComptableSvc = inject(CompteComptableService);
  #regimeSvc = inject(RegimeAlimentaireService);

  // --- Ressources ---
  famillesEgalim = computed(() => this.#familleEgalimSvc.famillesEgalimResource.value() ?? []);
  famillesFacturation = computed(() => this.#familleFacturationSvc.famillesFacturationResource.value() ?? []);

  #openDialogRequest = toSignal(this.#produitDeclinaisonSvc.openDialogProduitDeclinaison$);

  #dialogResource = resource<ProduitDeclinaisonDTO, DialogProduitDeclinaisonSupplier>({
    params: () => this.#openDialogRequest(),
    loader: ({params}) => {
      if (!params) return Promise.resolve(null);
      return this.#produitDeclinaisonSvc.prepareFields(params.produit, params.produitDeclinaison)
        .pipe(take(1))
        .toPromise()
        .then(response => response.produitDeclinaison)
        .catch(error => {
          this.#toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.ERROR, 'Impossible de charger la déclinaison de produit');
          throw error;
        });
    }
  });

  // --- État du produit déclinaison / formulaire (dérivé de l'ouverture de la dialog) ---
  produit = computed(() => this.#openDialogRequest()?.produit ?? null);
  produitDeclinaison = computed(() => this.#dialogResource.value() ?? null);
  allGefDefaultValues = computed(() => this.#openDialogRequest()?.allGefDefaultValues ?? []);

  isReadonly = computed(() => {
    const dto = this.produitDeclinaison();
    return dto ? !this.#produitDeclinaisonSvc.canModify(dto) : false;
  });
  isLockedAfterCreation = computed(() => this.produitDeclinaison() != null && this.produitDeclinaison().id !== 0);

  /**
   * Dans le cadre de la GEF : si le produit déclinée possède une déclinaison définie par défaut
   * (voir configuration GEF) alors il est possible de la modifier malgré le verrou post-création.
   */
  declinaisonLocked = computed(() => {
    if (!this.isLockedAfterCreation()) return false;
    if (!this.#auth2Svc.hasFeature(this.#auth2Svc.utilisateur, HAS_GEF)) return true;
    return !this.allGefDefaultValues().includes(this.produitDeclinaison()?.declinaison?.code);
  });

  // --- Listes de référence pour les lookups ---
  declinaisonList = computed(() => this.#declinaisonsSvc.getDeclinaisonList(this.#declinaisonsSvc.declinaisonEnvironnement, this.produitDeclinaison()?.fabrique ?? false));
  compteComptableList = computed(() => (this.#compteComptableSvc.comptesComptablesResource.value() ?? [])
    .map(cc => ({...cc, libelleEtCode: `${cc.libelle} - ${cc.code}`})));

  taxeList = () => this.#taxesSvc.taxesEnvironnement;
  uniteDeMesureList = () => this.#udmSvc.unitesDeMesureEnvironnement;
  zoneDeStockageList = () => this.#zdsSvc.zdsEnvironnement;
  tacheApprovisionnementList = () => this.#tacheSvc.tacheApprovisionnementList;
  tacheSortieList = () => this.#tacheSvc.tacheSortieList;
  tacheFabricationList = () => this.#tacheSvc.tacheFabricationList;
  contraintesAlimList = () => this.#regimeSvc.getRegimesContraintes()
    .map(c => ({...c, id: `${c.regime.id}_${c.texture.id}`}) as ContrainteModelWithId);

  formData = linkedSignal(() => {
    const dto = this.produitDeclinaison();
    if (!dto) return null;

    return untracked(() => {
      const inverseRatioTechnique = Number(ProduitDeclinaisonService.fixedValue(ProduitDeclinaisonService.calculeInverse(dto.ratioUniteTechnique), 4));
      const regimesSelected = this.#regimeSvc.preselectContraintesAlim(this.contraintesAlimList(), dto.produitDeclinaisonContrainteAlimList ?? []) as ContrainteModelWithId[];

      return {
        libelle: dto.libelle,
        libellelong: dto.libellelong,
        code: dto.code,
        reference: dto.reference,
        codealim: dto.codeAlim,
        actif: dto.actif,
        complementaire: dto.complementaire,
        ratioUniteTechnique: dto.ratioUniteTechnique,
        inverseRatioTechnique,
        ratioVolume: dto.ratioVolume,
        dlc: dto.dlc,
        prixDeVente: dto.prixDeVente,
        stockMinimum: dto.stockMinimum,
        declinaison: dto.id === 0 ? this.utils.getFirstElement(this.declinaisonList()) : this.declinaisonList().find(d => d.id === dto.declinaison?.id) ?? null,
        compteComptable: this.compteComptableList().find(cc => cc.id === dto.compteComptableId) ?? null,
        taxe: dto.id === 0 ? this.utils.getFirstElement(this.taxeList()) : this.taxeList().find(t => t.id === dto.taxe?.id) ?? null,
        uniteDeMesure: dto.uniteDeMesure ? this.uniteDeMesureList().find(u => u.id === dto.uniteDeMesure.id) ?? dto.uniteDeMesure : null,
        zoneDeStockage: this.zoneDeStockageList().find(z => z.id === dto.zoneDeStockage?.id) ?? null,
        tacheApprovisionnement: this.#findTacheSelected(dto, TYPE_TACHE.APPROVISIONNEMENT, this.tacheApprovisionnementList()),
        tacheSortie: this.#findTacheSelected(dto, TYPE_TACHE.SORTIE, this.tacheSortieList()),
        tacheFabrication: this.#findTacheSelected(dto, TYPE_TACHE.FABRICATION, this.tacheFabricationList()),
        familleEgalim: this.famillesEgalim().find(f => f.id === dto.familleEgalimId) ?? null,
        familleFacturation: this.famillesFacturation().find(f => f.id === dto.familleFacturationId) ?? null,
        regimesIds: regimesSelected.map(c => c.id)
      } as DeclinaisonFormData;
    });
  });

  uniteDeMesureWarning = linkedSignal<ProduitDeclinaisonDTO, string>({
    source: this.produitDeclinaison,
    computation: () => null
  });

  #uniteDeMesureLibelle = computed(() => this.formData()?.uniteDeMesure?.libelle?.toUpperCase() ?? '');
  ratioUniteTechniqueLabel = computed(() => `Ratio unité technique (${this.#uniteDeMesureLibelle()}/KG)`);
  ratioUniteTechniqueLabelPlat = computed(() => `Ratio unité technique (${this.#uniteDeMesureLibelle()} poids brut / KG)`);
  inverseRatioTechniqueLabel = computed(() => `Ratio unité universelle (KG/${this.#uniteDeMesureLibelle()})`);
  ratioUniteTechniquePrefix = computed(() => `1 ${this.#uniteDeMesureLibelle()} équivaut à`);
  inverseRatioTechniquePrefix = computed(() => `1 KG équivaut à`);
  prixSuffix = computed(() => `€ / ${this.#uniteDeMesureLibelle()}`);
  ratioVolumeRequired = computed(() => ProduitsService.isRatioVolumeRequired(this.formData()?.uniteDeMesure));
  canDelete = computed(() => this.gds.canDelete(this.produitDeclinaison()));

  // --- Données transmises aux enfants ---
  denreeData = computed<DeclinaisonDenreeData>(() => ({
    isReadonly: this.isReadonly(),
    isLockedAfterCreation: this.isLockedAfterCreation(),
    multiSites: this.multiSites(),
    prixSuffix: this.prixSuffix(),
    dlc: this.formData()?.dlc,
    stockMinimum: this.formData()?.stockMinimum,
    ratioUniteTechnique: this.formData()?.ratioUniteTechnique,
    inverseRatioTechnique: this.formData()?.inverseRatioTechnique,
    ratioVolume: this.formData()?.ratioVolume,
    prixDeVente: this.formData()?.prixDeVente,
    compteComptableList: this.compteComptableList(),
    compteComptableSelected: this.formData()?.compteComptable,
    taxeList: this.taxeList(),
    taxeSelected: this.formData()?.taxe,
    uniteDeMesureList: this.uniteDeMesureList(),
    uniteDeMesureSelected: this.formData()?.uniteDeMesure,
    ratioUniteTechniqueLabel: this.ratioUniteTechniqueLabel(),
    inverseRatioTechniqueLabel: this.inverseRatioTechniqueLabel(),
    ratioUniteTechniquePrefix: this.ratioUniteTechniquePrefix(),
    inverseRatioTechniquePrefix: this.inverseRatioTechniquePrefix(),
    uniteDeMesureWarning: this.uniteDeMesureWarning(),
    ratioVolumeRequired: this.ratioVolumeRequired(),
    zoneDeStockageList: this.zoneDeStockageList(),
    zoneDeStockageSelected: this.formData()?.zoneDeStockage,
    tacheApprovisionnementList: this.tacheApprovisionnementList(),
    tacheApprovisionnementSelected: this.formData()?.tacheApprovisionnement,
    tacheSortieList: this.tacheSortieList(),
    tacheSortieSelected: this.formData()?.tacheSortie,
    famillesEgalim: this.famillesEgalim(),
    familleEgalimSelected: this.formData()?.familleEgalim
  }));

  platData = computed<DeclinaisonPlatData>(() => ({
    isReadonly: this.isReadonly(),
    isLockedAfterCreation: this.isLockedAfterCreation(),
    prixSuffix: this.prixSuffix(),
    dlc: this.formData()?.dlc,
    ratioUniteTechnique: this.formData()?.ratioUniteTechnique,
    inverseRatioTechnique: this.formData()?.inverseRatioTechnique,
    ratioVolume: this.formData()?.ratioVolume,
    prixDeVente: this.formData()?.prixDeVente,
    compteComptableList: this.compteComptableList(),
    compteComptableSelected: this.formData()?.compteComptable,
    taxeList: this.taxeList(),
    taxeSelected: this.formData()?.taxe,
    famillesFacturation: this.famillesFacturation(),
    familleFacturationSelected: this.formData()?.familleFacturation,
    uniteDeMesureList: this.uniteDeMesureList(),
    uniteDeMesureSelected: this.formData()?.uniteDeMesure,
    ratioUniteTechniqueLabel: this.ratioUniteTechniqueLabelPlat(),
    inverseRatioTechniqueLabel: this.inverseRatioTechniqueLabel(),
    ratioUniteTechniquePrefix: this.ratioUniteTechniquePrefix(),
    inverseRatioTechniquePrefix: this.inverseRatioTechniquePrefix(),
    uniteDeMesureWarning: this.uniteDeMesureWarning(),
    ratioVolumeRequired: this.ratioVolumeRequired(),
    tacheFabricationList: this.tacheFabricationList(),
    tacheFabricationSelected: this.formData()?.tacheFabrication,
    contraintesAlimList: this.contraintesAlimList(),
    regimesSelectedIds: this.formData()?.regimesIds
  }));

  // --- Ouverture / fermeture du dialogue ---

  displayDialog = linkedSignal(() => {
    const data = this.#openDialogRequest();
    return data != null && this.produitDeclinaison() != null;
  });

  closeDialog = () => {
    this.displayDialog.set(false);
  };

  showChangeUniteTechnique = signal(false);

  openDialogChangeUniteTechnique = (): void => {
    this.showChangeUniteTechnique.set(true);
  };

  onNewUniteTechnique = (pd: ProduitDeclinaisonDTO): void => {
    this.#dialogResource.set({
      ...this.produitDeclinaison(),
      uniteDeMesure: pd.uniteDeMesure,
      ratioUniteTechnique: pd.ratioUniteTechnique
    });
  };

  #findTacheSelected(dto: ProduitDeclinaisonDTO, typeTache: TYPE_TACHE, list: TacheDTO[]): TacheDTO {
    const pt = _find(dto.produitDeclinaison__tacheList, {typeTacheCode: typeTache});
    if (this.utils.isNullOrEmpty(pt)) return null;
    return list.find(t => t.id === pt.tacheId) ?? null;
  }

  // --- Handlers des lookups (bindings des composants enfants) ---

  onChangeDeclinaison = (id: number) => this.formData.update(d => ({...d, declinaison: this.declinaisonList().find(x => x.id === id) ?? null}));

  onChangeCompteComptable = (id: number) => this.formData.update(d => ({...d, compteComptable: this.compteComptableList().find(x => x.id === id) ?? null}));

  onChangeTaxe = (id: number) => this.formData.update(d => ({...d, taxe: this.taxeList().find(x => x.id === id) ?? null}));

  onChangeZoneDeStockage = (id: number) => this.formData.update(d => ({...d, zoneDeStockage: this.zoneDeStockageList().find(x => x.id === id) ?? null}));

  onChangeTacheApprovisionnement = (id: number) => this.formData.update(d => ({...d, tacheApprovisionnement: this.tacheApprovisionnementList().find(x => x.id === id) ?? null}));

  onChangeTacheSortie = (id: number) => this.formData.update(d => ({...d, tacheSortie: this.tacheSortieList().find(x => x.id === id) ?? null}));

  onChangeTacheFabrication = (id: number) => this.formData.update(d => ({...d, tacheFabrication: this.tacheFabricationList().find(x => x.id === id) ?? null}));

  onChangeFamilleEgalim = (id: number) => this.formData.update(d => ({...d, familleEgalim: this.famillesEgalim().find(x => x.id === id) ?? null}));

  onChangeFamilleFacturation = (id: number) => this.formData.update(d => ({...d, familleFacturation: this.famillesFacturation().find(x => x.id === id) ?? null}));

  onChangeRegimes = (ids: (string | number)[]) => this.formData.update(d => ({...d, regimesIds: ids.map(id => `${id}`)}));

  onChangeUniteDeMesure = (id: number) => {
    const uniteDeMesure = this.uniteDeMesureList().find(u => u.id === id) ?? null;
    this.formData.update(d => ({...d, uniteDeMesure}));

    if (!ProduitDeclinaisonService.creatingNewProduitDeclinaison
      && uniteDeMesure?.id !== ProduitDeclinaisonService.uniteDeMesureInitiale?.id) {
      this.uniteDeMesureWarning.set(WARNING_UNITE_DE_MESURE);
    } else {
      this.uniteDeMesureWarning.set(null);
    }
  };

  // Parse la saisie (0 si invalide) et calcule sa valeur inverse arrondie à 4 décimales.
  #sanitizeAndInvert = (value: string): [number, number] => {
    let parsed = parseFloat(value);
    if (isNaN(parsed)) parsed = 0;

    const inverse = Number(ProduitDeclinaisonService.fixedValue(ProduitDeclinaisonService.calculeInverse(parsed), 4));
    return [parsed, inverse];
  };

  onChangeRatioUniteTechnique = (value: string) => {
    const [ratio, inverse] = this.#sanitizeAndInvert(value);
    this.formData.update(d => ({...d, ratioUniteTechnique: ratio, inverseRatioTechnique: inverse}));
  };

  onChangeInverseRatioUniteTechnique = (value: string) => {
    const [inverse, ratio] = this.#sanitizeAndInvert(value);
    this.formData.update(d => ({...d, inverseRatioTechnique: inverse, ratioUniteTechnique: ratio}));
  };

  // --- Handlers des champs de saisie (formData) ---

  onChangeLibelle = (value: string) => this.formData.update(d => ({...d, libelle: value}));
  onChangeLibelleLong = (value: string) => this.formData.update(d => ({...d, libellelong: value}));
  onChangeCode = (value: string) => this.formData.update(d => ({...d, code: value}));
  onChangeReference = (value: string) => this.formData.update(d => ({...d, reference: value}));
  onChangeCodeAlim = (value: number) => this.formData.update(d => ({...d, codealim: value}));
  onChangeActif = (value: boolean) => this.formData.update(d => ({...d, actif: value}));
  onChangeComplementaire = (value: boolean) => this.formData.update(d => ({...d, complementaire: value}));
  onChangeDlc = (value: number) => this.formData.update(d => ({...d, dlc: value}));
  onChangeStockMinimum = (value: number) => this.formData.update(d => ({...d, stockMinimum: value}));
  onChangeRatioVolume = (value: number) => this.formData.update(d => ({...d, ratioVolume: value}));
  onChangePrixDeVente = (value: number) => this.formData.update(d => ({...d, prixDeVente: value}));

  // --- Enregistrement ---

  // Équivaut à Validators.required : rejette null, undefined et chaîne vide.
  #isFilled = (value: unknown): boolean => value != null && `${value}`.trim() !== '';

  isFormValid = computed<boolean>(() => {
    const d = this.formData();
    if (!d) return false;

    const fieldsValid = !!d.libelle && d.libelle.length >= 2 && d.libelle.length <= 100
      && !!d.libellelong && d.libellelong.length >= 2 && d.libellelong.length <= 255
      && (!d.code || d.code.length <= 50)
      && (!d.reference || d.reference.length <= 50)
      && d.ratioUniteTechnique != null && d.ratioUniteTechnique >= 0.0001
      && d.inverseRatioTechnique != null && d.inverseRatioTechnique >= 0.0001
      && (!this.ratioVolumeRequired() || this.#isFilled(d.ratioVolume))
      && this.#isFilled(d.dlc);
    if (!fieldsValid) return false;

    if (!d.declinaison || !d.taxe || !d.uniteDeMesure) return false;

    if (!this.produitDeclinaison().fabrique) {
      return !!d.zoneDeStockage && !!d.tacheApprovisionnement && !!d.tacheSortie;
    }
    return !!d.tacheFabrication && d.regimesIds.length > 0;
  });

  save = () => {
    if (!this.isFormValid()) {
      this.#toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.ERROR, 'Veuillez compléter le formulaire');
      return;
    }

    const prepared = this.produit().typeProduit.fabrique
      ? this.#prepareSavePlat(this.produitDeclinaison())
      : this.#prepareSaveDenree(this.produitDeclinaison());

    this.#produitDeclinaisonSvc.saveProduitDeclinaison(prepared).subscribe(response => {
      // response est null si le mode opératoire est invalide (toast déjà affiché par checkValiditeModeOperatoire)
      if (!response) return;

      if (!this.utils.isResponseSupplierError(response)) {
        this.#produitDeclinaisonSvc.announceSavedDTO(response);
        this.#dialogResource.set(response.one);
        this.#toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `L'enregistrement de la déclinaison de produit ${response.one.libelle.toUpperCase()} ${response.one.declinaison.libelle.toUpperCase()} a été réalisée avec succès`);
        this.#routeMapSvc.goToSecondaryRoute(['gestionproduits', 'produit', this.produit().typeProduit.fabrique, this.produit().id, 'produit-declinaisons']);
      } else {
        this.#toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.ERROR, response.resultMessage);
      }
    });
  };

  #assignCommonSaveFields(produitDeclinaison: ProduitDeclinaisonDTO): void {
    const f = this.formData();

    produitDeclinaison.libelle = f.libelle;
    produitDeclinaison.libellelong = f.libellelong;
    produitDeclinaison.code = f.code;
    produitDeclinaison.reference = f.reference;
    produitDeclinaison.actif = f.actif;
    produitDeclinaison.complementaire = f.complementaire;
    produitDeclinaison.declinaison = f.declinaison;
    produitDeclinaison.codeAlim = f.codealim;

    produitDeclinaison.compteComptableId = f.compteComptable?.id ?? null;
    produitDeclinaison.prixDeVente = f.prixDeVente;
    produitDeclinaison.taxe = f.taxe;

    produitDeclinaison.uniteDeMesure = f.uniteDeMesure;
    produitDeclinaison.ratioUniteTechnique = f.ratioUniteTechnique;
    produitDeclinaison.ratioVolume = f.ratioVolume;
    produitDeclinaison.dlc = f.dlc;
  }

  #prepareSavePlat(produitDeclinaison: ProduitDeclinaisonDTO): ProduitDeclinaisonDTO {
    const f = this.formData();
    this.#assignCommonSaveFields(produitDeclinaison);

    produitDeclinaison.familleFacturationId = f.familleFacturation?.id ?? null;

    const regimesSelected = this.contraintesAlimList().filter(c => f.regimesIds.includes(c.id));
    produitDeclinaison.produitDeclinaisonContrainteAlimList = this.#prepareSaveRegimes(regimesSelected);

    const ptFabrication = this.#produitDeclinaisonSvc.initTacheOfProduitDeclinaison(produitDeclinaison, f.tacheFabrication);
    produitDeclinaison.produitDeclinaison__tacheList = [ptFabrication];

    produitDeclinaison.zoneDeStockage = null;

    return produitDeclinaison;
  }

  #prepareSaveDenree(produitDeclinaison: ProduitDeclinaisonDTO): ProduitDeclinaisonDTO {
    const f = this.formData();
    this.#assignCommonSaveFields(produitDeclinaison);

    produitDeclinaison.zoneDeStockage = f.zoneDeStockage;
    const pdtAppro = this.#produitDeclinaisonSvc.initTacheOfProduitDeclinaison(produitDeclinaison, f.tacheApprovisionnement);
    if (!this.multiSites()) produitDeclinaison.stockMinimum = f.stockMinimum;

    const pdtSortie = this.#produitDeclinaisonSvc.initTacheOfProduitDeclinaison(produitDeclinaison, f.tacheSortie);

    produitDeclinaison.familleEgalimId = f.familleEgalim?.id ?? null;

    produitDeclinaison.produitDeclinaison__tacheList = [pdtSortie, pdtAppro];
    return produitDeclinaison;
  }

  #prepareSaveRegimes(selected: ContrainteModel[]): ProduitDeclinaisonContrainteAlimDTO[] {
    const produitDeclinaisonContrainteAlimList: ProduitDeclinaisonContrainteAlimDTO[] = [];

    if (!this.utils.isNullOrEmpty(selected)) {
      for (const contrainte of selected) {
        const contrainteDto = new ProduitDeclinaisonContrainteAlimDTO();
        contrainteDto.id = 0;
        contrainteDto.produitDeclinaisonLibelle = this.produitDeclinaison().libelle;
        contrainteDto.produitDeclinaisonId = 0;
        contrainteDto.textureId = contrainte.texture.id;
        contrainteDto.regimeId = contrainte.regime.id;
        produitDeclinaisonContrainteAlimList.push(contrainteDto);
      }
    }

    return produitDeclinaisonContrainteAlimList;
  }

  // --- Aides de template / divers ---

  getTitle = (produitDeclinaison: ProduitDeclinaisonDTO): string => {
    if (this.utils.isNullOrEmpty(produitDeclinaison)) return '';
    if (produitDeclinaison.id === 0) {
      return `<i class="fas fa-plus mg-r-5"></i>Créer une déclinaison de ${produitDeclinaison.fabrique ? 'plat' : 'denrée'}`;
    }
    return `<i class="fas fa-edit mg-r-5"></i> ${produitDeclinaison.libelle.toUpperCase()}`;
  };

  openDeleteDialog = async (produitDeclinaison: ProduitDeclinaisonDTO) => {
    const message = this.#textMessageService.getProduitDeletionMessage(produitDeclinaison);
    const isDeleted = await confirm(message, 'Suppression de produit déclinaison');
    if (!isDeleted) return;

    this.#produitDeclinaisonSvc.deleteById(produitDeclinaison).subscribe((response: ResponseWrapper<IsDeletableObject>) => {
      this.utils.deleteMessage(response, MSG_KEY.ROOT);
      if (response.one.deletable) {
        this.deleted.emit(produitDeclinaison.id);
        this.closeDialog();
      }
    });
  };

  multiSites = (): boolean => this.#auth2Svc.utilisateur.siteListLocaux.length > 1;

  openGestionStockMini() {
    this.#stockSvc.announceOpenGestionStockMini(this.produitDeclinaison());
  }
}
